import {noop} from '@benchling/js-utils';
import {List} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  isFileConfigVariable,
  isImportPlatesConfigVariable,
} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileChip from 'files/chips/FileChip';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {GetAnalysesForAssayRun_analyses_AnalysisNode as AnalysisNode} from 'results/analysis-integration/__generated/GetAnalysesForAssayRun';
import {
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode_configVariables_AnalysisTemplateVariableNode as AnalysisTemplateVariableNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {isVariableRequired} from 'runs/assay-group/AnalysisTemplateIntegrationRun';
import {AssayRunField} from 'runs/assay-group/AssayRunFields';
import BooleanValueEditor from 'schemas/tags/BooleanValueEditor';
import SearchInput from 'search/inputs/SearchInput';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ChipSize} from 'ui-library/chips/Chip';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import {JSONValue} from 'util/Types';

interface VariableBindingFieldProps {
  variable: AnalysisTemplateVariableNode;
  value: JSONValue;
}

function VariableBindingField({variable, value}: VariableBindingFieldProps): JSX.Element {
  const variableTypeConfigSpec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;

  // Get the scalar config spec - for arrays, use elementSpec; otherwise use the spec itself
  const scalarConfigSpec =
    variableTypeConfigSpec.kind === 'array' ? variableTypeConfigSpec.elementSpec : variableTypeConfigSpec;
  const isMulti = variableTypeConfigSpec.kind === 'array';

  let inputComponent: JSX.Element;

  switch (scalarConfigSpec.kind) {
    case 'boolean': {
      const booleanValue = typeof value === 'boolean' ? value : null;
      inputComponent = <BooleanValueEditor defaultValue={booleanValue} onUpdate={noop} isDisabled={true} />;
      break;
    }
    case 'integer': {
      const stringValue =
        value != null && (typeof value === 'number' || typeof value === 'string') ? String(value) : '';
      inputComponent = (
        <NumericInput
          value={stringValue}
          onChange={noop}
          onKeyDown={noop}
          validateInteger={true}
          isDisabled={true}
        />
      );
      break;
    }
    case 'number': {
      const stringValue =
        value != null && (typeof value === 'number' || typeof value === 'string') ? String(value) : '';
      inputComponent = (
        <NumericInput value={stringValue} onChange={noop} onKeyDown={noop} isDisabled={true} />
      );
      break;
    }
    case 'string': {
      const stringValue = value != null && typeof value === 'string' ? value : '';
      inputComponent = <TextInput value={stringValue} onChange={noop} disabled={true} />;
      break;
    }
    case 'domain': {
      // For domain types (entity links), use SearchInput in read-only mode
      const selectedIds = Array.isArray(value)
        ? value.filter((v): v is string => typeof v === 'string')
        : typeof value === 'string'
          ? [value]
          : [];

      inputComponent = (
        <SearchInput
          autoFocus={false}
          disableTyping={true}
          disabled={true}
          types={[ModelTypeConstants.BASIC_FOLDER_ITEM]} // Generic type for entities
          selected={selectedIds}
          onSelect={noop}
          onRemove={noop}
          placeholder=""
          isMulti={isMulti}
        />
      );
      break;
    }
    default: {
      // Fallback for unsupported types
      const displayValue =
        value != null ? (typeof value === 'string' ? value : JSON.stringify(value, null, 2)) : '';
      inputComponent = <TextInput value={displayValue} onChange={noop} disabled={true} />;
    }
  }

  return (
    <AssayRunField title={variable.name} key={variable.id} isRequired={isVariableRequired(variable)}>
      {inputComponent}
    </AssayRunField>
  );
}

interface FileBindingFieldProps {
  variable: AnalysisTemplateVariableNode;
  value: JSONValue;
}

function FileBindingField({variable, value}: FileBindingFieldProps): JSX.Element {
  // Extract file IDs from the value (can be string, array of strings, or null)
  const fileIds: Array<string> = Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : typeof value === 'string'
      ? [value]
      : [];

  return (
    <AssayRunField title={variable.name} key={variable.id} isRequired={isVariableRequired(variable)}>
      {fileIds.length === 0 ? (
        <div className="resultsAttachment-assayRun-emptyFieldValue" />
      ) : (
        <div className={css(styles.fileChipsWrapper)}>
          {fileIds.map((fileId) => (
            <FileChip key={fileId} id={fileId} size={ChipSize.SMALL} />
          ))}
        </div>
      )}
    </AssayRunField>
  );
}

function PlateBindingField({variable, value}: VariableBindingFieldProps): JSX.Element {
  const plateIds = Array.isArray(value)
    ? value.filter((plateId): plateId is string => typeof plateId === 'string')
    : [];
  return (
    <AssayRunField title={variable.name} key={variable.id} isRequired={isVariableRequired(variable)}>
      {plateIds.length === 0 ? (
        <div className="resultsAttachment-assayRun-emptyFieldValue" />
      ) : (
        <List type="ordered">
          {plateIds.map((plateId) => (
            <List.Item key={plateId}>{plateId}</List.Item>
          ))}
        </List>
      )}
    </AssayRunField>
  );
}

function sortVariables(variables: Array<AnalysisTemplateVariableNode>): Array<AnalysisTemplateVariableNode> {
  return [...variables].sort((a, b) => {
    const aRequired = isVariableRequired(a);
    const bRequired = isVariableRequired(b);

    // Primary sort: required fields first
    if (aRequired !== bRequired) {
      return aRequired ? -1 : 1;
    }

    // Secondary sort: alphabetically by name
    return a.name.localeCompare(b.name);
  });
}

interface ViewAnalysisInputsModalProps {
  onRequestClose: () => void;
  analysis: AnalysisNode;
  analysisTemplate: AnalysisTemplateNode;
}

export default function ViewAnalysisInputsModal({
  onRequestClose,
  analysis,
  analysisTemplate,
}: ViewAnalysisInputsModalProps): JSX.Element {
  const variableBindings =
    analysis.createdFromTemplateInformation?.inputs ?? (null as Record<string, JSONValue> | null);

  // Some required variables may be missing values, since an analysis created before the all_variable_bindings
  // backfill will not have an explicit record of the non-FILE inputs used to created itself
  const isMissingRequiredVariableValues = useMemo(() => {
    if (variableBindings == null) {
      return false;
    }
    return analysisTemplate.configVariables.some((variable) => {
      const value = variableBindings[variable.id];
      return isVariableRequired(variable) && value == null;
    });
  }, [analysisTemplate.configVariables, variableBindings]);

  // Separate file/non-file variables, and sort each subgroup (a) by whether the variable is required and
  // (b) alphabetically
  const {nonFileVariables, fileVariables, plateVariables} = useMemo(() => {
    const nonFiles: Array<AnalysisTemplateVariableNode> = [];
    const files: Array<AnalysisTemplateVariableNode> = [];
    const plates: Array<AnalysisTemplateVariableNode> = [];

    analysisTemplate.configVariables.forEach((variable) => {
      if (isImportPlatesConfigVariable(variable)) {
        plates.push(variable);
      } else if (isFileConfigVariable(variable)) {
        files.push(variable);
      } else {
        nonFiles.push(variable);
      }
    });

    return {
      nonFileVariables: sortVariables(nonFiles),
      fileVariables: sortVariables(files),
      plateVariables: sortVariables(plates),
    };
  }, [analysisTemplate.configVariables]);

  return (
    <Modal
      enableRedesign={true}
      title={analysis.name}
      onRequestClose={onRequestClose}
      buttons={[{text: 'Close', cancel: true}]}
    >
      <div>
        {isMissingRequiredVariableValues && (
          <div className={css(styles.warningBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              supportingInfo="Values could not be found for one or more required inputs. For complete information about how this analysis was originally configured, please consult the analysis's audit logs."
            />
          </div>
        )}
        {variableBindings != null && (
          <>
            {nonFileVariables.length > 0 && (
              <Row bufferRowReset={true}>
                {nonFileVariables.map((variable, i) => {
                  const value = variableBindings[variable.id];
                  return (
                    <div key={`col-${variable.id}`}>
                      {i % 3 === 0 && <Col className="clearfix" key={`clearfix-${i}`} />}
                      <Col className="resultsAttachment-assayRun-fieldSection" sm={4} key={i}>
                        <VariableBindingField variable={variable} value={value} />
                      </Col>
                    </div>
                  );
                })}
              </Row>
            )}
            {nonFileVariables.length > 0 && (fileVariables.length > 0 || plateVariables.length > 0) && <hr />}
            {(fileVariables.length > 0 || plateVariables.length > 0) && (
              <div className={css(styles.fileVariablesWrapper)}>
                {plateVariables.map((variable) => (
                  <div key={variable.id} className={css(styles.fileVariableField)}>
                    <PlateBindingField variable={variable} value={variableBindings[variable.id]} />
                  </div>
                ))}
                {fileVariables.map((variable) => {
                  const value = variableBindings[variable.id];
                  return (
                    <div key={variable.id} className={css(styles.fileVariableField)}>
                      <FileBindingField variable={variable} value={value} />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  warningBanner: {
    marginBottom: 24,
  },
  fileVariablesWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  fileVariableField: {
    display: 'flex',
    flexDirection: 'column',
  },
  fileChipsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 8,
  },
});
